import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";

const SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    body: [
      "ThinkRight Tech Pvt. Ltd. (\"ThinkRight\", \"we\", \"us\") respects your privacy. This Privacy Policy explains what personal information we collect when you visit thinkright-tech.com, apply for a role, or contact us, and how we use, share and protect that information.",
      "This policy is written to meet our obligations under the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (India), the EU General Data Protection Regulation (GDPR), and the California Consumer Privacy Act (CCPA), to the extent they apply.",
    ],
  },
  {
    id: "controller",
    title: "Data controller",
    body: [
      "The data controller for personal information collected through this website is ThinkRight Tech Pvt. Ltd. with registered offices in Chandigarh, New York and Bengaluru (BHIVE Workspace, No. 112, AKR Tech Park 'A' & 'B' Block, 7th Mile Hosur Rd, Kudlu Gate, Krishna Reddy Industrial Area, Bengaluru, Karnataka 560068).",
      "For any privacy query, write to contact@thinkright-tech.com.",
    ],
  },
  {
    id: "what",
    title: "What we collect",
    body: [
      "Application data — when you apply for a role, we collect your name, email, phone, years of experience, LinkedIn and portfolio URLs, any resume link you share, and your cover note.",
      "Contact data — when you write to us, we collect your name, email, company, role and the message you send.",
      "Technical data — standard request metadata (IP address, user agent, timestamp) is logged briefly for security and abuse-prevention, then discarded within 30 days.",
      "Cookies / local storage — we use a single first-party localStorage entry to remember your cookie-consent choice, and a JWT token in localStorage only if you are an admin signed in to our ATS. We do not set any third-party analytics, advertising or social-media tracking cookies by default.",
    ],
  },
  {
    id: "why",
    title: "Why we collect it",
    body: [
      "To evaluate your application and communicate with you about the role you applied for (lawful basis: consent + pre-contractual steps under GDPR Art. 6(1)(b) / 6(1)(a)).",
      "To respond to your partnership, press or general inquiry (lawful basis: legitimate interest).",
      "To secure the website and prevent fraud, brute-force login attempts and abuse (lawful basis: legitimate interest).",
    ],
  },
  {
    id: "share",
    title: "Who we share it with",
    body: [
      "Employees of ThinkRight Tech and, where relevant, hiring managers of the partner portfolio company the role sits with.",
      "Our infrastructure sub-processors: MongoDB Atlas (database hosting), Resend (email delivery), and our cloud host. Each operates under a written data-processing agreement.",
      "We do not sell, rent or trade personal information.",
    ],
  },
  {
    id: "retention",
    title: "How long we keep it",
    body: [
      "Applications are retained for up to 24 months to consider you for future openings, unless you ask us to delete them sooner.",
      "Contact inquiries are retained for up to 12 months for relationship continuity.",
      "Admin audit logs are retained for 12 months for security purposes.",
    ],
  },
  {
    id: "rights",
    title: "Your rights",
    body: [
      "You have the right to access, correct, port, restrict, object to, or delete your personal information.",
      "For EU/UK residents under GDPR, you also have the right to withdraw consent at any time and to lodge a complaint with your local supervisory authority.",
      "For California residents under CCPA, you have the right to know, to delete, and to opt out of any sale (we do not sell personal information).",
      "To exercise any right, email contact@thinkright-tech.com — we will respond within 30 days.",
    ],
  },
  {
    id: "transfers",
    title: "International transfers",
    body: [
      "Data may be processed in India, the United States and the European Union depending on where our sub-processors operate. Where data leaves the EEA/UK, we rely on Standard Contractual Clauses and equivalent safeguards.",
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "We use TLS in transit, encryption at rest, bcrypt-hashed admin credentials, JWT-based authentication, principle-of-least-privilege access controls, and audit logging.",
      "No system is perfectly secure — if you suspect a vulnerability, write to contact@thinkright-tech.com and we will respond within 48 hours.",
    ],
  },
  {
    id: "cookies",
    title: "Cookies & local storage",
    body: [
      "The only first-party storage we set by default is a single key remembering your cookie-consent choice. Until you accept non-essential storage, we will not store any optional analytics or marketing identifiers.",
      "You can clear site data at any time from your browser settings.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: [
      "We may update this policy from time to time. Material changes will be announced at the top of this page and, where required, via email to contacts we have on file.",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="bg-bone">
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">Privacy Policy</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Your data,
              <br />
              <span className="italic text-ink/60">handled plainly.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              We collect only what we need, keep it only as long as we need, and
              never sell it.
            </p>
            <div className="mt-8 text-sm text-ink/60">
              Last updated: 20 February 2026
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16">
          <aside
            className="col-span-12 md:col-span-4 md:sticky md:top-24 md:self-start"
            data-testid="privacy-toc"
          >
            <div className="overline text-ink/50 mb-4">Contents</div>
            <ol className="space-y-2 text-sm border-t border-border pt-4">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex items-baseline gap-3 py-1 text-ink/70 hover:text-ink"
                  >
                    <span className="font-mono text-xs text-ink/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <div className="col-span-12 md:col-span-8 space-y-16">
            {SECTIONS.map((s, i) => (
              <Reveal
                key={s.id}
                id={s.id}
                delay={i}
                className="scroll-mt-28"
                as="section"
              >
                <div className="overline text-vermilion mb-3">
                  Section {String(i + 1).padStart(2, "0")}
                </div>
                <h2
                  id={s.id}
                  className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mb-6"
                >
                  {s.title}
                </h2>
                <div className="space-y-4 text-ink/80 leading-relaxed">
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </Reveal>
            ))}

            <div className="border-t border-border pt-8 text-sm text-ink/60">
              Questions about this policy? Write to{" "}
              <a
                href="mailto:contact@thinkright-tech.com"
                className="link-underline text-ink"
                data-testid="privacy-contact-email"
              >
                contact@thinkright-tech.com
              </a>
              .
            </div>
            <Link to="/" data-testid="privacy-home" className="btn-outline w-fit">
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
